import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { SurveyDefinition, APIResponse, SurveyListFilters } from '../types/survey.types';
import { retryFirebaseCall } from '../utils/retry';

export type SurveyStatusAction = 'activate' | 'pause' | 'resume' | 'close' | 'reopen';

interface UseSurveysReturn {
  loading: boolean;
  error: string | null;
  createSurvey: (survey: Partial<SurveyDefinition>) => Promise<string>;
  updateSurvey: (surveyId: string, updates: Partial<SurveyDefinition>) => Promise<void>;
  getSurvey: (surveyId: string) => Promise<SurveyDefinition>;
  getSurveyByAccessCode: (accessCode: string) => Promise<SurveyDefinition>;
  listSurveys: (filters?: SurveyListFilters) => Promise<SurveyDefinition[]>;
  deleteSurvey: (surveyId: string) => Promise<void>;
  duplicateSurvey: (surveyId: string, newTitle?: string) => Promise<string>;
  archiveSurvey: (surveyId: string) => Promise<void>;
  unarchiveSurvey: (surveyId: string) => Promise<void>;
  setSurveyStatus: (surveyId: string, action: SurveyStatusAction) => Promise<string>;
}

export const useSurveys = (): UseSurveysReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new survey
   */
  const createSurvey = useCallback(async (survey: Partial<SurveyDefinition>): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const createSurveyFn = httpsCallable<Partial<SurveyDefinition>, APIResponse<string>>(
        functions,
        'createSurvey'
      );

      const result = await retryFirebaseCall(
        () => createSurveyFn(survey),
        'createSurvey'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to create survey');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create survey';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update an existing survey
   */
  const updateSurvey = useCallback(
    async (surveyId: string, updates: Partial<SurveyDefinition>): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const updateSurveyFn = httpsCallable<
          { surveyId: string; updates: Partial<SurveyDefinition> },
          APIResponse<void>
        >(functions, 'updateSurvey');

        const result = await retryFirebaseCall(
          () => updateSurveyFn({ surveyId, updates }),
          'updateSurvey'
        );

        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to update survey');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to update survey';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Get a single survey by ID
   */
  const getSurvey = useCallback(async (surveyId: string): Promise<SurveyDefinition> => {
    setLoading(true);
    setError(null);

    try {
      const getSurveyFn = httpsCallable<
        { surveyId: string },
        APIResponse<SurveyDefinition>
      >(functions, 'getSurvey');

      const result = await retryFirebaseCall(
        () => getSurveyFn({ surveyId }),
        'getSurvey'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to get survey');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get survey';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a survey by access code
   */
  const getSurveyByAccessCode = useCallback(async (accessCode: string): Promise<SurveyDefinition> => {
    setLoading(true);
    setError(null);

    try {
      const getSurveyByAccessCodeFn = httpsCallable<
        { accessCode: string },
        APIResponse<SurveyDefinition>
      >(functions, 'getSurveyByAccessCode');

      const result = await retryFirebaseCall(
        () => getSurveyByAccessCodeFn({ accessCode }),
        'getSurveyByAccessCode'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Survey not found');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get survey by access code';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List all surveys with optional filters
   */
  const listSurveys = useCallback(
    async (filters?: SurveyListFilters): Promise<SurveyDefinition[]> => {
      setLoading(true);
      setError(null);

      try {
        const listSurveysFn = httpsCallable<
          { filters?: SurveyListFilters },
          APIResponse<SurveyDefinition[]>
        >(functions, 'listSurveys');

        const result = await retryFirebaseCall(
          () => listSurveysFn({ filters }),
          'listSurveys'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || 'Failed to list surveys');
        }

        return result.data.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to list surveys';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Delete a survey
   */
  const deleteSurvey = useCallback(async (surveyId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const deleteSurveyFn = httpsCallable<
        { surveyId: string },
        APIResponse<void>
      >(functions, 'deleteSurvey');

      const result = await retryFirebaseCall(
        () => deleteSurveyFn({ surveyId }),
        'deleteSurvey'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to delete survey');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete survey';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Duplicate a survey
   */
  const duplicateSurvey = useCallback(
    async (surveyId: string, newTitle?: string): Promise<string> => {
      setLoading(true);
      setError(null);

      try {
        const duplicateSurveyFn = httpsCallable<
          { surveyId: string; newTitle?: string },
          APIResponse<string>
        >(functions, 'duplicateSurvey');

        const result = await retryFirebaseCall(
          () => duplicateSurveyFn({ surveyId, newTitle }),
          'duplicateSurvey'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || 'Failed to duplicate survey');
        }

        return result.data.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to duplicate survey';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Archive a survey
   */
  const archiveSurvey = useCallback(async (surveyId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const archiveSurveyFn = httpsCallable<
        { surveyId: string },
        APIResponse<void>
      >(functions, 'archiveSurvey');

      const result = await retryFirebaseCall(
        () => archiveSurveyFn({ surveyId }),
        'archiveSurvey'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to archive survey');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to archive survey';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Unarchive a survey
   */
  const unarchiveSurvey = useCallback(async (surveyId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const unarchiveSurveyFn = httpsCallable<
        { surveyId: string },
        APIResponse<void>
      >(functions, 'unarchiveSurvey');

      const result = await retryFirebaseCall(
        () => unarchiveSurveyFn({ surveyId }),
        'unarchiveSurvey'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to unarchive survey');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to unarchive survey';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Lifecycle action: activate / pause / resume / close / reopen.
   * Server stamps launchedAt (first activate) and closedAt (close) with server timestamps,
   * and validates that the requested transition is legal for the survey's current state.
   * Returns the new derived state ('active' | 'paused' | 'closed' | 'draft').
   */
  const setSurveyStatus = useCallback(
    async (surveyId: string, action: SurveyStatusAction): Promise<string> => {
      setLoading(true);
      setError(null);

      try {
        const setSurveyStatusFn = httpsCallable<
          { surveyId: string; action: SurveyStatusAction },
          APIResponse<{ state: string }>
        >(functions, 'setSurveyStatus');

        const result = await retryFirebaseCall(
          () => setSurveyStatusFn({ surveyId, action }),
          'setSurveyStatus'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || `Failed to ${action} survey`);
        }

        return result.data.data.state;
      } catch (err: any) {
        const errorMessage = err.message || `Failed to ${action} survey`;
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    createSurvey,
    updateSurvey,
    getSurvey,
    getSurveyByAccessCode,
    listSurveys,
    deleteSurvey,
    duplicateSurvey,
    archiveSurvey,
    unarchiveSurvey,
    setSurveyStatus,
  };
};
